import { Moment } from 'moment-timezone';

export enum PricingFilterSectionKind {
  DateRange = 'dateRange',
  Pill = 'pill',
  Range = 'range',
}

// Isolates filter selections per screen so two screens with overlapping
// section ids (e.g. both use 'edits') never read or write each other's state.
export enum PricingFilterScope {
  AgentReview = 'agentReview',
  Bookmarked = 'bookmarked',
  CostChanges = 'costChanges',
  CustomerDetail = 'customerDetail',
  Customers = 'customers',
  ItemCostChanges = 'itemCostChanges',
  Review = 'review',
}

export enum PricingFilterUnit {
  Currency = 'currency',
  Percent = 'percent',
  Unit = 'unit',
}

export enum PricingFilterSpecialSectionId {
  Bookmarked = 'bookmarked',
  Category = 'category',
  Edits = 'edits',
  PeerAverage = 'peerAverage',
  Status = 'status',
}

export enum PricingEditsFilterValue {
  Has = 'has',
  None = 'none',
}

export enum PricingBookmarkedFilterValue {
  Bookmarked = 'bookmarked',
  NotBookmarked = 'not_bookmarked',
}

export enum PricingPeerAverageFilterValue {
  Above = 'above',
  Below = 'below',
}

export enum PricingLastInvoiceFilterValue {
  Custom = 'custom',
  Never = 'never',
  Recent = 'recent',
}

export interface PricingFilterOption {
  label: string;
  value: string;
}

export interface PillFilterSection<T = unknown> {
  getValues: (row: T) => string[];
  id: string;
  kind: PricingFilterSectionKind.Pill;
  options: PricingFilterOption[];
  singleSelect: boolean;
  title: string;
}

export interface RangeFilterSection<T = unknown> {
  affix: string;
  bounds: { max: number; min: number };
  getValue: (row: T) => number | null;
  id: string;
  kind: PricingFilterSectionKind.Range;
  title: string;
  toggleGroupId?: string;
  unit: PricingFilterUnit;
}

export interface DateRangeFilterSection<T = unknown> {
  getDate: (row: T) => Moment | null;
  id: string;
  kind: PricingFilterSectionKind.DateRange;
  title: string;
}

export type PricingFilterSection<T = unknown> =
  | PillFilterSection<T>
  | RangeFilterSection<T>
  | DateRangeFilterSection<T>;

export interface PricingRangeBound {
  max: number | null;
  min: number | null;
}

export interface PricingRangeColumnConfig<T = unknown> {
  accessor: (row: T) => number | null;
  affix: string;
  filterPillMetric?: (row: T) => number | null;
  title: string;
  unit: PricingFilterUnit;
}

export interface PricingFilterSelections {
  pills: Record<string, string[]>;
  ranges: Record<string, PricingRangeBound>;
}
